<script lang="ts">
	import RouteFeaturesStructure from '$lib/components/city/features/route/RouteFeaturesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { SELF_GUIDED_TOUR_CATEGORY_LABELS } from '$lib/features/selfGuidedTour/selfGuidedTourCategory.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { getTopFrequentValues } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.SELF_GUIDED_TOUR;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const meta = $derived.by(() => {
		const firstAvailableImage: CoverImage | undefined = output.items.flatMap((ride) => ride.stops.flatMap((s) => s.images)).at(0);
		const itemsLength = output.items.length;
		const toursWording = itemsLength === 1 ? 'tour' : 'tours';
		const descriptionNames =
			itemsLength === 1
				? output.items[0].feature.englishName
				: itemsLength === 2
					? `from ${output.items.map((item) => item.feature.englishName).join(' to ')}`
					: `from ${output.items
							.slice(0, 2)
							.map((item) => item.feature.englishName)
							.join(' to ')} & more`;
		const totalDurationSeconds = output.items.reduce((acc, curr) => acc + curr.route.durationSeconds, 0);
		const hoursTotal = Math.round(totalDurationSeconds / 3600);
		const hourDescription = hoursTotal === 1 ? '1 hour' : `${hoursTotal} hours`;
		const topCategories = getTopFrequentValues(
			output.items.map((i) => i.categories),
			2
		).map((e) => SELF_GUIDED_TOUR_CATEGORY_LABELS[e]);
		return {
			title: `${location.city.label} Self-Guided Tours: From ${topCategories.join(' to ')} Trails`,
			description: `Discover ${location.city.label} with ${itemsLength} self-guided walking ${toursWording} you can do at your own pace, ${descriptionNames} — ${hourDescription} total.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<RouteFeaturesStructure items={output.items} {location} language={output.countryLanguage} keepExploring={output.keepExploring}>
	{#snippet HeaderSnippet()}
		<HeroNeutral
			title={`Best Self-Guided Tours of ${location.city.label}`}
			description={`Explore on your terms. Follow our ${output.items.length} expert-crafted routes to uncover the hidden stories, history, and culture of the city—no tour guide required.`}
			breadcrumbs={data.output.breadcrumbs}
			decorations={{
				hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
			}}
		/>
	{/snippet}
</RouteFeaturesStructure>
